import { Dialog, Box, TextField } from '@mui/material';
import { AxiosError } from 'axios';
import { FC, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import { getChangingModelText } from 'entities/CustomModel/api';
import { getChangingText } from 'entities/ImportSessions/api';
import { showErrorNotification } from 'shared/helpers/showErrorNotification';
import { ActionsForDialog } from 'shared/ui/ActionsForDialog';
import { Input } from 'shared/ui/Input';
import { Paper } from 'shared/ui/Paper';
import { Region } from 'components/file-editor-components/file-viewer/region-types';

type FieldLabelModalProps = {
	fileId?: string;
	region: Region | null;
	onCancel: () => void;
	onSave: (label: { name: string; text: string }) => void;
	mode?: 'custom' | 'general';
};

const FieldLabelModal: FC<FieldLabelModalProps> = ({
	fileId,
	region,
	onCancel,
	onSave,
	mode = 'general',
}) => {
	const { t } = useTranslation();
	const [labelName, setLabelName] = useState('');
	const [regionText, setRegionText] = useState('');

	const payload = useMemo(
		() => ({
			id: fileId,
			data: {
				pageNumber: (region?.pageIndex ?? 0) + 1,
				boxPosition: {
					x: region?.x ?? 0,
					y: region?.y ?? 0,
					width: region?.width ?? 0,
					height: region?.height ?? 0,
				},
			},
		}),
		[fileId, region],
	);

	const { isLoading } = useQuery({
		queryKey: [
			'get/rectangle-content',
			region?.pageIndex,
			region?.x,
			region?.y,
			region?.width,
			region?.height,
		],
		queryFn: () => (mode === 'custom' ? getChangingModelText(payload) : getChangingText(payload)),
		onSuccess: (response) => {
			setRegionText((response?.data as string) || '');
		},
		onError: (err) => {
			showErrorNotification(err as AxiosError);
		},
		enabled: !!region && !!fileId,
	});

	useEffect(() => {
		if (!region) return;
		setLabelName('');
		setRegionText('');
	}, [region]);

	const handleCancel = () => {
		setLabelName('');
		setRegionText('');
		onCancel();
	};

	const handleSave = () => {
		if (!labelName) return;
		onSave({ name: labelName, text: regionText });
		setLabelName('');
		setRegionText('');
	};

	return (
		<Dialog
			open={Boolean(region)}
			onClose={handleCancel}
			PaperComponent={Paper}
			PaperProps={{ title: t('files.details.add_label') as string }}
		>
			<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
				<Input
					sx={{ margin: 0 }}
					name={'labelName'}
					label={t('files.labels.label_name')}
					placeholder={t('files.placeholder.label_name') as string}
					type={'text'}
					required
					onChange={(event) => setLabelName(event.target.value)}
					value={labelName}
				/>
				<TextField
					placeholder={
						isLoading
							? (t('shared_texts.loading') as string)
							: (t('files.placeholder.text_inside_label') as string)
					}
					value={regionText}
					multiline
					maxRows={6}
					variant={'outlined'}
					disabled={isLoading}
					onChange={(event) => setRegionText(event.target.value)}
				/>
				<ActionsForDialog
					applyActionDisabled={!labelName}
					cancelLabel={t('shared_texts.cancel')}
					cancelAction={handleCancel}
					applyLabel={t('shared_texts.save')}
					applyAction={handleSave}
				/>
			</Box>
		</Dialog>
	);
};

export default FieldLabelModal;
